import moment from 'moment';
import { formatPhoneNumber } from 'common/utils';
import {
    Box, TableCell
} from "@mui/material";
import { Grid, MenuItem, TextField } from "@material-ui/core";
import { store } from 'app';
import React, { useEffect } from "react";
import AppSnackbar from 'components/snackbar/snackbar';
import SimpleBackdrop from 'common/components/loaders/simple-backdrop-loader';
import { AutomationConstant, AutomationType } from '../api/automation-constants';
import AutomationAPI, { AutomationEmailLogRequest, AutomationEmailLogResponse, AutomationTaskLogRequest, AutomationTextLogResponse, GetTaskLogResponse } from '../api/automation-api';
import { BaseResponse } from 'common/utils/shared/shared-models';
import ReusableTable from './reusable-table';
import axios, { CancelTokenSource } from 'axios';

const commonTextFieldProps = {
    fullWidth: true,
    variant: "outlined" as const,
};
const automationTypeOptions = Object.keys(AutomationType)
    .filter((key) => isNaN(Number(key)))
    .map((key) => ((
        <MenuItem key={AutomationType[key as keyof typeof AutomationType]} value={AutomationType[key as keyof typeof AutomationType]}>
            {key}
        </MenuItem>
    )));


const AutomationAnalytics = () => {

    const [showSnackbar] = AppSnackbar.useSnackBar();

    // #region Automation Logs State
    const [autoType, setAutoType] = React.useState<number>(AutomationType.Email);
    const [data, setData] = React.useState<{
        smsLogs: AutomationTextLogResponse[] | null;
        emailLogs: AutomationEmailLogResponse[] | null;
        taskLogs?: GetTaskLogResponse[] | null;
    }>({
        smsLogs: null,
        emailLogs: null,
        taskLogs: null,
    });

    // #endregion Automation Logs State
    useEffect(() => {
        const getAnalyticsToken = axios.CancelToken.source();
        callAutomationAnalytics(getAnalyticsToken);
        return () => {
            getAnalyticsToken.cancel('Request canceled due to component unmount or parameter change.');
        };
    }, [autoType])

    const callAutomationAnalytics = async (getAnalyticsToken:CancelTokenSource): Promise<void> => {
        switch (autoType) {
            case AutomationType.Email:
                await GetEmailLogs(getAnalyticsToken);
                break;
            case AutomationType.Text:
                await GetTextLogs(getAnalyticsToken);
                break;
            case AutomationType.Task:
                await GetTaskLogs(getAnalyticsToken);
                break;
            default:
                break;
        }
    }
    const [logsBackdropLoading, setLogsBackdropLoading] = React.useState(false);

    // #region Automation Logs

    const handleError = (error: any, fallbackMessage: string): void => {
        const errorMessage = error?.errorDetails?.[0] || error?.message || error?.error || fallbackMessage;
        showSnackbar({ type: 'error', message: errorMessage });
    };

    async function GetEmailLogs(getAnalyticsToken:CancelTokenSource): Promise<void> {
        setLogsBackdropLoading(true);
        try {
            let textReq: AutomationEmailLogRequest = {
                dmsDealerId: (store.getState().user as { dmsDealerId: number }).dmsDealerId,
            }
            const response: BaseResponse<Array<AutomationEmailLogResponse>> = await AutomationAPI.GetEmailLogs(textReq,getAnalyticsToken.token);
            if (!response.isSuccess) {
                handleError(response, AutomationConstant.unableToFetchLogs);
                return;
            }
            setData(prev => ({
                ...prev,
                emailLogs: response.data,
                smsLogs: null,
                taskLogs: null,
            }));

        } catch (error: unknown) {
            if (axios.isCancel(error)) return;
            const errorMessage = error instanceof Error ? error.message : AutomationConstant.unableToFetchLogs;
            showSnackbar({
                type: 'error',
                message: errorMessage,
            });
        } finally {
            setLogsBackdropLoading(false);
        }
    }

    async function GetTextLogs(getAnalyticsToken:CancelTokenSource): Promise<void> {
        setLogsBackdropLoading(true);
        try {
            let textReq: AutomationEmailLogRequest = {
                dmsDealerId: (store.getState().user as { dmsDealerId: number }).dmsDealerId,
            }
            const response: BaseResponse<Array<AutomationTextLogResponse>> = await AutomationAPI.GetTextLogs(textReq,getAnalyticsToken.token);
            if (!response.isSuccess) {
                handleError(response, AutomationConstant.unableToFetchLogs);
                return;
            }
            setData(prev => ({
                ...prev,
                emailLogs: null,
                smsLogs: response.data,
                taskLogs: null,
            }));

        } catch (error: unknown) {
            if (axios.isCancel(error)) return;
            const errorMessage = error instanceof Error ? error.message : AutomationConstant.unableToFetchLogs;
            showSnackbar({
                type: 'error',
                message: errorMessage,
            });
        } finally {
            setLogsBackdropLoading(false);
        }
    }

    async function GetTaskLogs(getAnalyticsToken:CancelTokenSource): Promise<void> {
        setLogsBackdropLoading(true);
        try {
            let taskReq: AutomationTaskLogRequest = {
                dmsDealerId: (store.getState().user as { dmsDealerId: number }).dmsDealerId,
            }
            const response: BaseResponse<Array<GetTaskLogResponse>> = await AutomationAPI.GetTaskLogs(taskReq,getAnalyticsToken.token);
            if (!response.isSuccess) {
                handleError(response, AutomationConstant.unableToFetchLogs);
                return;
            }
            setData(prev => ({
                ...prev,
                emailLogs: null,
                smsLogs: null,
                taskLogs: response.data,
            }));

        } catch (error: unknown) {
            if (axios.isCancel(error)) return;
            const errorMessage = error instanceof Error ? error.message : AutomationConstant.unableToFetchLogs;
            showSnackbar({
                type: 'error',
                message: errorMessage,
            });
        } finally {
            setLogsBackdropLoading(false);
        }
    }

    // #endregion Automation Logs
    const dateFormat = 'M/D/Y h:mm a';


    return (
        <Box style={{ marginTop: "10px" }}>

            <Grid container spacing={2} alignItems="center" justify="flex-end" style={{ marginTop: 16, marginBottom: 8 }}>
                <Grid item xs={12}>
                    <Box display="flex" justifyContent="flex-end">
                        <TextField
                            select
                            {...commonTextFieldProps}
                            name="autoType"
                            label="Automation Type"
                            size="small"
                            value={autoType}
                            onChange={(e) => setAutoType(Number(e.target.value))}
                            style={{ width: 180 }}
                        >
                            {automationTypeOptions}
                        </TextField>
                    </Box>
                </Grid>
            </Grid>


            <ReusableTable
                columns={['Date', 'Department', 'From', 'To', 'Message', 'Job#', 'Status']}
                data={data.smsLogs}
                renderRow={(row) => (
                    <>
                        <TableCell valign='top'>{moment.utc(row.dateCreated).local().format(dateFormat)}</TableCell>
                        <TableCell valign='top' >{row.departmentName}</TableCell>
                        <TableCell valign='top' style={{ whiteSpace: 'nowrap' }}>{formatPhoneNumber(row.from)}</TableCell>
                        <TableCell valign='top'><span style={{ whiteSpace: 'nowrap' }}>{formatPhoneNumber(row.to)}</span> ({row.status})</TableCell>
                        <TableCell valign='top'>{row.body}</TableCell>
                        <TableCell valign='top'>{row.jobId}</TableCell>
                        <TableCell valign='top'>{row.orderStatus}</TableCell>
                    </>
                )}
            />

            <ReusableTable
                columns={['Date', 'Email', 'InvoiceId', 'Job#', 'Status']}
                data={data.emailLogs}
                renderRow={(row) => (
                    <>
                        <TableCell valign='top'>{moment.utc(row.dateCreated).local().format(dateFormat)}</TableCell>
                        <TableCell valign='top' >{row.emailAddress}</TableCell>
                        <TableCell valign='top'>{row.invoiceID}</TableCell>
                        <TableCell valign='top'>{row.jobId}</TableCell>
                        <TableCell valign='top'>{row.orderStatus}</TableCell>
                    </>
                )}
            />





            <ReusableTable
                columns={['Date', 'Invoice#', 'Job#', 'Status', 'Task Subject', 'Assigned User']}
                data={data.taskLogs}
                renderRow={(row: GetTaskLogResponse) => (
                    <>
                        <TableCell valign="top">{moment.utc(row.dateCreated).local().format(dateFormat)}</TableCell>
                        <TableCell valign="top">{row.invoiceId}</TableCell>
                        <TableCell valign="top">{row.jobId}</TableCell>
                        <TableCell valign="top">{row.orderStatus}</TableCell>
                        <TableCell valign="top">{row.taskSubject}</TableCell>
                        <TableCell valign="top">{row.assignedUser}</TableCell>
                    </>
                )}
            />



            {logsBackdropLoading && (<SimpleBackdrop />)}
        </Box>
    );
}


export default AutomationAnalytics;